import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';

import '../ids-text/ids-text';
import '../ids-alert/ids-alert';
import '../ids-icon/ids-icon';
import '../ids-hyperlink/ids-hyperlink';
import IdsButton from '../ids-button/ids-button';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsElement from '../../core/ids-element';

import styles from './ids-notification-banner.scss';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import type IdsText from '../ids-text/ids-text';

const Base = IdsKeyboardMixin(
  IdsEventsMixin(
    IdsElement
  )
);

export type IdsNotificationBannerAlertType = 'success' | 'warning' | 'caution' | 'info' | 'error';

export interface IdsNotificationBannerSettings {
  /* Set the id for the column group object */
  id?: string;
  /* Set the parent selector of where to append */
  parent?: string;
  /* Set the alert type */
  type: IdsNotificationBannerAlertType;
  /* Set the message contents */
  messageText?: string | null | undefined;
  /* Set the href link */
  link?: string,
  /* Set the href link text */
  linkText?: string,
  target?: '_self' | '_blank';

  closeButtonSettings?: {
    audibleText?: string;
    describedBy?: string;
  }
}

/**
 * IDS Notification Banner
 * @type {IdsNotificationBanner}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsKeyboardMixin
 * @part container - the notification banner element
 * @part message - the message inside the container element
 * @part link - the link inside the container element
 * @part button - the close button inside the container element
 */
@customElement('ids-notification-banner')
@scss(styles)
export default class IdsNotificationBanner extends Base {
  constructor() {
    super();
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#attachEventHandlers();
    this.#attachKeyboardListeners();
  }

  /**
   * Return the properties we handle as getters/setters
   * @returns {Array} The properties in an array
   */
  static get attributes(): Array<any> {
    return [
      attributes.MESSAGE,
      attributes.MESSAGE_TEXT,
      attributes.LINE_CLAMP,
      attributes.LINK,
      attributes.LINK_TEXT,
      attributes.TYPE,
      attributes.WRAP,
      attributes.TARGET,
      attributes.HREF,
    ];
  }

  #handlers: Record<string, (newValue: string) => void> = {
    [attributes.MESSAGE]: (v) => this.#messageText(v),
    [attributes.MESSAGE_TEXT]: (v) => this.#messageText(v),
    [attributes.TARGET]: (v) => this.#updateLinkTarget(v),
    [attributes.LINK]: (v) => {
      this.#updateLink(v);
      this.#updateLinkVisibility();
    },
    [attributes.LINK_TEXT]: (v) => {
      this.#updateLinkText(v);
      this.#updateLinkVisibility();
    }
  };

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (oldValue === newValue) return;
    this.#handlers[name]?.(newValue);
  }

  #messageText(value: string) {
    const textElement = this.container?.querySelector<IdsText>('.ids-notification-banner-message ids-text.message');
    if (textElement) textElement.innerText = value || '';
  }

  // update the  link dynamically
  #updateLink(value: string) {
    const linkElement = this.container?.querySelector<IdsText>('.ids-notification-banner-link ids-hyperlink');
    (linkElement as any).href = value || '';
  }

  /**
   * Updates the hyperlink text content
   * @param {string} value the link text to display
   */
  #updateLinkText(value: string) {
    const linkElement = this.container?.querySelector<HTMLElement>('.ids-notification-banner-link ids-hyperlink');
    if (linkElement) linkElement.innerText = value || 'Click to view';
  }

  /** Toggles link container visibility based on whether link or linkText is set */
  #updateLinkVisibility() {
    const linkContainer = this.container?.querySelector('.ids-notification-banner-link');
    linkContainer?.toggleAttribute(attributes.HIDDEN, !this.link && !this.linkText);
  }

  // update the target dynamically
  #updateLinkTarget(value: string) {
    const linkElement = this.container?.querySelector('.ids-notification-banner-link ids-hyperlink');
    (linkElement as any).target = value === 'self' ? '_self' : '_blank';
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    // Set the alert icon based on the notification type
    const alertIcon = this.type || 'success';
    const type = (!this.type) ? 'success' : this.type;
    const overflow = this.wrap ? '' : 'overflow="ellipsis"';
    const closeAudibleText = this.closeLabelAudible || 'Close Button';

    return `
      <div class="ids-notification-banner" part="container" type="${type}">
        <ids-alert icon="${alertIcon === 'warning' ? 'alert' : alertIcon}"></ids-alert>
        <div class="ids-notification-banner-message ${this.wrap ? 'wrap' : ''}" part="message">
          <ids-text class="message" ${overflow ? 'tooltip="true"' : ''} ${overflow}>${this.messageText !== null ? this.messageText : 'Enter Message Text.'}</ids-text>
        </div>

        <div class="ids-notification-banner-link" part="link"${!this.link && !this.linkText ? ' hidden' : ''}>
          <ids-hyperlink font-size="16" href="${this.link || ''}" target="${this.target || '_blank'}">${this.linkText === null ? 'Click to view' : this.linkText}</ids-hyperlink>
        </div>

        <div class="ids-notification-banner-button" part="button">
          <ids-button appearance="tertiary">
            <ids-icon icon="close" size="small"></ids-icon>
            <span class="audible">${closeAudibleText}</span>
          </ids-button>
        </div>
      </div>
    `;
  }

  /**
   * Toggle text wrapping for overflowing messages.
   * Text overflow style is ellipsis by default.
   * @param {boolean | string} value wrapText value
   */
  set wrap(value: boolean | string) {
    const messageContainer = this.container?.querySelector('.ids-notification-banner-message');
    const messageText = messageContainer?.querySelector('ids-text');

    if (stringToBool(value)) {
      this.setAttribute(attributes.WRAP, '');
      messageContainer?.classList.add('wrap');
      messageText?.setAttribute(attributes.OVERFLOW, 'none');
      messageText?.removeAttribute(attributes.TOOLTIP);
    } else {
      this.removeAttribute(attributes.WRAP);
      messageContainer?.classList.remove('wrap');
      messageText?.setAttribute(attributes.OVERFLOW, 'ellipsis');
      messageText?.setAttribute(attributes.TOOLTIP, 'true');
    }
  }

  get wrap(): boolean | string {
    return stringToBool(this.getAttribute(attributes.WRAP));
  }

  /**
   * Set the type of the Notification Banner
   * @param {IdsNotificationBannerAlertType | null} value the type value
   * success, alert, info, error
   */
  set type(value: IdsNotificationBannerAlertType | null) {
    if (!value) {
      this.removeAttribute(attributes.TYPE);
      this.setAttribute(attributes.TYPE, 'success');
      this.container?.setAttribute(attributes.TYPE, 'success');
      this.container?.querySelector<HTMLElement>('ids-alert')!.setAttribute('icon', 'success');
    } else {
      this.setAttribute(attributes.TYPE, value);
      this.container?.setAttribute(attributes.TYPE, value);
      const alertIcon = value === 'warning' ? 'alert' : value;
      this.container?.querySelector<HTMLElement>('ids-alert')!.setAttribute('icon', alertIcon || 'info');
    }
  }

  get type(): IdsNotificationBannerAlertType | null {
    return this.getAttribute(attributes.TYPE) as IdsNotificationBannerAlertType;
  }

  /**
   * Set the link inside the Notification Banner
   * @param {string | null} value the link value
   */
  set link(value: string | null) {
    if (value) {
      this.setAttribute(attributes.LINK, value);
    } else {
      this.removeAttribute(attributes.LINK);
    }
    this.#updateLink(value || '');
    this.#updateLinkVisibility();
  }

  get link(): string | null { return this.getAttribute(attributes.LINK); }

  /**
   * Sets line clamp value to truncate message text
   * @param {number|null} val number of displayed before truncating
   */
  set lineClamp(val: number | null) {
    const rows = Number(val);

    if (!Number.isNaN(rows) && rows > 0) {
      this.setAttribute(attributes.LINE_CLAMP, String(rows));
      this.messageElem?.setAttribute(attributes.LINE_CLAMP, String(rows));
    } else {
      this.removeAttribute(attributes.LINE_CLAMP);
      this.messageElem?.removeAttribute(attributes.LINE_CLAMP);
    }
  }

  get lineClamp(): number | null {
    return Number(this.getAttribute(attributes.LINE_CLAMP)) || null;
  }

  /**
   * Set the custom link text of the Notification Banner
   * @param {string | null} value the link-text value
   */
  set linkText(value: string | null) {
    if (value) {
      this.setAttribute(attributes.LINK_TEXT, value);
    } else {
      this.removeAttribute(attributes.LINK_TEXT);
    }
    this.#updateLinkText(value || '');
    this.#updateLinkVisibility();
  }

  get linkText() { return this.getAttribute(attributes.LINK_TEXT); }

  /**
   * Set the audible text for the close button
   * @param {string} value the audible text value
   */
  set closeLabelAudible(value: string) {
    this.setAttribute(attributes.CLOSE_LABEL_AUDIBLE, value);

    if (value.length === 0) return;

    const closeBtn = this.container?.querySelector<IdsButton>('ids-button');
    if (closeBtn) closeBtn.labelAudible = value;
  }

  get closeLabelAudible(): string | null {
    return this.getAttribute(attributes.CLOSE_LABEL_AUDIBLE) || null;
  }

  /**
   * Set the target attribute for the link
   * @param {string | null} value the target value (_self or _blank)
   */
  set target(value: string | null) {
    const linkElem = this.container?.querySelector('ids-hyperlink');
    if (value) {
      this.setAttribute(attributes.TARGET, value);
      linkElem?.setAttribute(attributes.TARGET, value);
    } else {
      this.removeAttribute(attributes.TARGET);
      linkElem?.removeAttribute(attributes.TARGET);
    }
  }

  get target(): string | null { return this.getAttribute(attributes.TARGET); }

  /**
   * Set the href attribute for the link
   * @param {string | null} value the href value
   */
  set href(value: string | null) {
    const linkElem = this.container?.querySelector('ids-hyperlink');
    if (value) {
      this.setAttribute(attributes.HREF, value);
      linkElem?.setAttribute(attributes.HREF, value);
    } else {
      this.removeAttribute(attributes.HREF);
      linkElem?.removeAttribute(attributes.HREF);
    }
  }

  get href(): string | null { return this.getAttribute(attributes.HREF); }

  /**
   * Set the message text of the Notification Banner
   * @param {string | null} value the message-text value
   */
  set messageText(value: string | null) {
    if (value) {
      this.setAttribute(attributes.MESSAGE_TEXT, value);
    } else {
      this.removeAttribute(attributes.MESSAGE_TEXT);
    }
  }

  get messageText() { return this.getAttribute(attributes.MESSAGE_TEXT); }

  /**
   * Set the message text of the Notification Banner
   * @param {string | null} value the message-text value
   */
  set message(value: string | null) {
    if (value) {
      this.setAttribute(attributes.MESSAGE, value);
    } else {
      this.removeAttribute(attributes.MESSAGE);
    }
  }

  get message() { return this.getAttribute(attributes.MESSAGE); }

  get messageElem(): IdsText | null {
    return this.container?.querySelector<IdsText>('ids-text.message') ?? null;
  }

  /**
   * Establish Internal Event Handlers
   * @private
   * @returns {object} The object for chaining.
   */
  #attachEventHandlers(): object {
    const closeBtn = this.container?.querySelector('ids-button');
    const link = this.container?.querySelector('ids-hyperlink');
    this.onEvent('click', closeBtn, () => {
      this.dismiss();
    });
    this.onEvent('click', link, (event: any) => {
      if (this.link) this.triggerEvent('link-clicked', this, { detail: { elem: this, event } });
    });
    return this;
  }

  /**
   * Establish Internal Keyboard shortcuts
   * @private
   * @returns {object} This API object for chaining
   */
  #attachKeyboardListeners(): object {
    const closeBtn = this.container?.querySelector('ids-button');
    this.listen('Link Clicked', closeBtn, () => this.dismiss());
    return this;
  }

  /**
   * Shows a notification banner dynamically
   * @param {object} notification Object passed in for notification creation
   * @returns {void}
   */
  add(notification: IdsNotificationBannerSettings): void {
    const {
      id,
      parent,
      type,
      messageText,
      link,
      linkText
    } = notification;
    const messageTextEl = this.container?.querySelector('[part="message"]');
    const linkEl = this.container?.querySelector('[part="link"]');
    const alertIcon = this.container?.querySelector('ids-alert');
    const overflow = this.wrap ? '' : 'overflow="ellipsis"';
    const closeBtn = this.container?.querySelector<IdsButton>('ids-button');

    // Set properties
    if (id) {
      this.setAttribute('id', id);
    }
    this.type = type;
    this.messageText = messageText || '';
    alertIcon?.setAttribute('icon', this.type ?? '');
    if (messageTextEl) messageTextEl.innerHTML = `<ids-text ${overflow}>${this.messageText}</ids-text>`;

    // Set close button
    if (notification.closeButtonSettings && closeBtn) {
      const closeSettings = notification.closeButtonSettings;
      closeBtn.describedBy = closeSettings.describedBy || '';
      this.closeLabelAudible = closeSettings.audibleText || '';
    }

    // Check for link and create the necassary elements.
    if (notification.link && linkEl) {
      this.link = link || '';
      this.linkText = linkText === undefined ? 'Click to view' : linkText;
      linkEl.innerHTML = `<ids-hyperlink href="${this.link}" target="${notification.target || this.target || '_blank'}">${this.linkText}</ids-hyperlink>`;
    }

    // Check if parent container is defined if not append to a parent
    if (parent) {
      const parentEl = document.querySelector(parent);
      parentEl?.prepend(<any> this);
    } else if (document.querySelector('ids-container')) {
      document.querySelector('ids-container')?.prepend(<any> this);
    } else {
      document.body.prepend(<any> this);
    }
  }

  isServiceBanner = false;

  /**
   * Show the notification banner
   * @param {IdsNotificationBannerSettings} settings The settings for the notification banner
   */
  show(settings?: IdsNotificationBannerSettings) {
    if (settings) {
      this.isServiceBanner = true;
      this.add(settings);
    }
  }

  /**
   * Remove the notification from the page
   */
  dismiss() {
    let canDismiss = true;
    const response = (veto: any) => {
      canDismiss = !!veto;
    };
    this.triggerEvent('beforeclose', this, { detail: { elem: this, response } });

    if (!canDismiss) {
      return;
    }

    this.triggerEvent('close', this, { detail: { elem: this } });
    this.remove();
    this.triggerEvent('afterclose', this, { detail: { elem: this } });

    if (this.isServiceBanner) {
      this.remove();
    }
  }
}
